import React from "react";
import "./skeleton.scss";

// One shimmering placeholder. Every other export here is a composition of
// these, so the animation lives in exactly one place (skeleton.scss) and the
// shimmer direction follows the page's `dir` rather than each caller's layout.
//
// Why not react-content-loader (already a dependency, used by
// components/grid_list/loader.js and friends): it renders an SVG per
// placeholder and animates a <linearGradient>, which means every loading
// screen ships its own hand-measured viewBox in SVG user units. These are
// plain divs, so they size themselves from the surrounding CSS and mirror
// under RTL for free.
export function SkeletonBlock({ width, height, radius, className, style }) {
  return (
    <div
      className={"skeleton-block" + (className ? " " + className : "")}
      style={{ width, height, borderRadius: radius, ...style }}
    />
  );
}

// A list or table: N rows of equal height. Use it wherever rows are about to
// arrive — it is the shape the jobs grid, the users panel and the shift board
// all land in.
export function SkeletonRows({ rows = 10, height = 75, className }) {
  return (
    <div
      className={"skeleton-rows" + (className ? " " + className : "")}
      aria-busy="true"
      aria-live="polite"
    >
      {Array.from({ length: rows }, (_, i) => (
        <SkeletonBlock key={i} height={height} />
      ))}
    </div>
  );
}

// A card: optional avatar/thumbnail, a title and `lines` text lines. `lines`
// is what makes it match the card underneath, so pass the real line count.
export function SkeletonCard({ lines = 3, media = true, className }) {
  return (
    <div
      className={"skeleton-card" + (className ? " " + className : "")}
      aria-busy="true"
    >
      {media && <SkeletonBlock className="skeleton-card__media" />}
      <div className="skeleton-card__body">
        <SkeletonBlock className="skeleton-card__title" />
        {Array.from({ length: lines }, (_, i) => (
          <SkeletonBlock
            key={i}
            className="skeleton-card__line"
            // Last line short, like real wrapped text.
            width={i === lines - 1 ? "60%" : "100%"}
          />
        ))}
      </div>
    </div>
  );
}

// The whole app frame: side rail, top bar, content rows. This is for the
// moment before any screen has been downloaded, where there is nothing on the
// display to match yet — the route-level <Suspense> fallbacks.
export function SkeletonPage({ rows = 8 }) {
  return (
    <div className="skeleton-page" aria-busy="true">
      <div className="skeleton-page__rail">
        {Array.from({ length: 7 }, (_, i) => (
          <SkeletonBlock key={i} className="skeleton-page__rail-item" />
        ))}
      </div>
      <div className="skeleton-page__main">
        <SkeletonBlock className="skeleton-page__topbar" />
        <SkeletonRows rows={rows} height={64} />
      </div>
    </div>
  );
}

// Fallback for a lazily-loaded modal (the create wizards, the job panel).
// Centred over the page, because the screen underneath is still the old one
// and a skeleton pasted into the flow would push it around.
export function SkeletonModal() {
  return (
    <div className="skeleton-modal" aria-busy="true">
      <SkeletonCard lines={4} media={false} />
    </div>
  );
}

export default SkeletonBlock;
